/*
 * Evaluation component specific for Adalia M&E.
 * This is a composite based on other components.
 */

import React from 'react';

import Base from './base.jsx'; 
import {
  Mode,
  DataType,
  castDataType,
} from '../common.jsx';
import Helpers from './helpers.jsx'
import Setting from "./setting.jsx";

import DropdownSelection from './dropdown_selection.jsx'
import LongTextField from './long_text_field.jsx'
import GridLayout from './grid_layout.jsx'
import Text from './text.jsx'

export default class AdaliaMEEvaluation extends GridLayout {

        static get CATEGORY() {
            return 'question';
        }

        static get TYPE() {
            return 'adalia_me_evaluation'
        }

        static defaultContent(language) {
            let fieldName = 'field_' + Helpers.randomString();
            return Object.assign(super.defaultContent(language), {
                name: fieldName,
                isPrescoring: false,
                rows: [
                    [
                        {
                            span: 4,
                            component: Object.assign(Text.defaultContent(language), {
                                text: I18n.t('evaluation_default_text', {scope: 'fe'}),
                                deletable: false,
                                sortable: false
                            })
                        },
                        {
                            span: 1,
                            component: AdaliaMEEvaluationWeight.defaultContent(language)
                        },
                        {
                            span: 1,
                            component: Object.assign(AdaliaMEEvaluationScore.defaultContent(language), {
                                name: fieldName
                            })
                        }
                    ],
                    [
                        {
                            span: 1,
                            component: Object.assign(AdaliaMEEvaluationComment.defaultContent(language), {
                                name: fieldName
                            })
                        }
                    ]
                ]
            });
        }

        constructor(props) {
            super(props);

            // Ensure old forms have also sortable = false for nested
            // components since that attribute was added later.
            if (this.props.mode == Mode.EDIT) {
                this.constructor.nestedComponents(this.props.content).forEach((c) => {
                    if (typeof c.sortable === 'undefined') {
                        c.sortable = false;
                    }
                });
            }
        }

        componentDidMount() {
            super.componentDidMount();

            if (typeof this.props.updateSettingsCallback == 'function') {
                let content = this.props.content;
                if (content.isNewComponent) {
                    content.rows[0].forEach((comp, i) => {
                        content.rows[0][i].component.isNewComponent = true;
                    })
                    content.rows[1][0].component.isNewComponent = true;
                    this.props.updateSettingsCallback(content.id, content, true);
                }
            }
        }

        containerClassNames() {
            let classNames = super.containerClassNames();

            if (this.props.content.isPrescoring) {
                classNames.push('prescoring');
            }
            return classNames;
        }

        renderSettings() {
            let isPrescoring = this.props.content.isPrescoring;

            // Since "isPrescoring" is a later addition the default is here set to
            // false for forms created before the change.
            if (typeof isPrescoring == 'undefined') {
                isPrescoring = false;
            }
            return (
                <div>
                    <Setting.Checkbox
                        name="isPrescoring"
                        label={I18n.t('field_is_prescoring', {scope: 'fe'})}
                        value={isPrescoring}
                        description={I18n.t('field_is_prescoring_description', {scope: 'fe'})}
                        onChange={this.updateSettings} />
                </div>
            )
        }

        beforeUpdateSettings(content, deletedRowIndex, deletedColIndex) {
            content.isPrescoring = castDataType(content.isPrescoring, DataType.BOOLEAN);
        }
}


/*
 * Evaluation Weight. This is meant for internal use of AdaliaMEEvaluation.
 */
export class AdaliaMEEvaluationWeight extends Base {
        static get TYPE() {
            return 'adalia_me_evaluation_weight';
        }

        static get ANSWER_DEPENDENT() {
          return false
        }


        static get TRANSLATED_ATTRIBUTES() {
            return {
                label: 'evaluation_weight'
            };
        }

        static defaultContent(language) {
            return Object.assign(super.defaultContent(language), {
                label: 'label',
                value: 1,
                deletable: false,
                sortable: false
            });
        }

        renderView() {
            return (
                <div>
                    <label>
                        {this.getText('label')}
                        <input type="text" className="weight form-control"
                            value={this.props.content.value || 0}
                            readOnly={true} />
                    </label>
                </div>
            );
        }

        renderSettings() {
            return (
                <div>
                    <Setting.TextField name="label" label={I18n.t('field_label', {scope: 'fe'})} value={this.getText('label')} onChange={this.updateSettings} />
                    <Setting.TextField name="value" label={I18n.t('field_value', {scope: 'fe'})} value={this.props.content.value} onChange={this.updateSettings} />
                </div>
            );
        }

        beforeUpdateSettings(content) {
            content.value = castDataType(content.value, DataType.DECIMAL);
        }
}


    /*
     * Evaluation Score. This is meant for internal use of AdaliaMEEvaluation.
     */
export class AdaliaMEEvaluationScore extends DropdownSelection {
        static get TYPE() {
            return 'adalia_me_evaluation_score';
        }

        static get DATA_TYPES() {
            return [
                DataType.INTEGER,
                DataType.DECIMAL
            ];
        }

        static defaultContent(language) {
            let content = Object.assign(super.defaultContent(language), {
                dataType: DataType.INTEGER,
                deletable: false,
                sortable: false
            });
            content.options[0].options = [];

            for (let i = 1; i <= 5; i++) {
                content.options[0].options.push({value: i, label: 'opt-' + i});
                content.translations[language] = content.translations[language] || {};
                content.translations[language]['opt-' + i] = '' + i;
            }
            return content;
        }

        fullFieldName() {
            return super.fullFieldName() + '[score]';
        }
}


    /*
     * Evaluation Comment. This is meant for internal use of AdaliaMEEvaluation.
     */
export class AdaliaMEEvaluationComment extends LongTextField {
        static get TYPE() {
            return 'adalia_me_evaluation_comment';
        }

        static defaultContent(language) {
            let content = Object.assign(super.defaultContent(language), {
                deletable: false,
                sortable: false,
                available: true
            });
            content.translations[language] = Object.assign(content.translations[language], {
                label: '',
                description: '',
                placeholder: I18n.t('evaluation_comment', {scope: 'fe', locale: language})
            });

            return content;
        }

        fullFieldName() {
            return super.fullFieldName() + '[comment]';
        }

        renderSettings() {
            let available = this.props.content.available;

            // Since "available" is a later addition the default is here set to
            // true for forms created before the change.
            if (typeof available == 'undefined') {
                available = true;
            }
            return (
                <div>
                    <Setting.Checkbox name="required" label={I18n.t('field_required', {scope: 'fe'})} value={this.props.content.required} onChange={this.updateSettings} />
                    <Setting.Checkbox name="available" label={I18n.t('available_when_composing', {scope: 'fe'})} value={available} onChange={this.updateSettings} />
                    <Setting.TextField name="label" label={I18n.t('field_label', {scope: 'fe'})} value={this.getText('label')} onChange={this.updateSettings} />
                    <Setting.Checkbox name="label_hidden" className="label-hidden-check" label={I18n.t('field_label_hide', {scope: 'fe'})} value={this.props.content.label_hidden} onChange={this.updateSettings} />
                    <Setting.TextField name="placeholder" label={I18n.t('field_placeholder', {scope: 'fe'})} value={this.getText('placeholder')} onChange={this.updateSettings} />
                    <Setting.TextField name="description" label={I18n.t('field_description', {scope: 'fe'})} value={this.getText('description')} onChange={this.updateSettings} />
                    <Setting.LongTextField name="help" label={I18n.t('field_help', {scope: 'fe'})} value={this.getText('help')} onChange={this.updateSettings} />
                    <Setting.TextField name="wordLimit" label={I18n.t('field_word_limit', {scope: 'fe'})} value={this.props.content.wordLimit} onChange={this.updateSettings} description={I18n.t('field_word_limit_description', {scope: 'fe'})} />
                    <Setting.TextField
                      name="reportKey"
                      label={I18n.t('field_report_key', {scope: 'fe'})}
                      value={this.props.content.reportKey}
                      description={I18n.t('field_report_key_description', {scope: 'fe'})}
                      {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
                    />
                </div>
            );
        }

        beforeUpdateSettings(content) {
            super.beforeUpdateSettings(content);
            content.available = castDataType(content.available, DataType.BOOLEAN);
        }
}
